/**
 * TodayFocusBanner — 스튜디오 홈 '오늘 챙길 일' 배너(useTodoSample → useTodos, scope=today — 오늘 할일만 조회).
 * 기획 studio-home-empty-0907-web(940 S14-1) 구성: 머리줄 / 빨강 상위 3건 / 더보기 줄(펼침 시 나머지 빨강 + 오늘 일정).
 * 줄·필터·건수는 나의 할일 화면과 같은 모듈(TodoRow·todoFilters·useTodoSample)을 쓴다. 담당자 선택도 공유(localStorage).
 * 조회 오류·신호 누락은 오류 상태로 보여주고 샘플로 덮지 않는다.
 * 카드 안 텍스트는 `.app-card p/h4` 규칙(components.css)을 피해 div·span·h3 만 쓴다.
 * 신호 점은 SignalDot(semantic.signal.* — Status 참조) — 점 옆에 항상 숫자·글자를 둔다(D-07 §1.2·§3.2 #5).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, Card, Skeleton, StatusHint, TextLink } from '@indipia/ui-core';
import { SignalMissingError } from '@indipia/shared';
import { useCompanyMembers } from '@indipia/shared-features';
import { SignalDot } from './SignalDot';
import { ChevronRightIcon, TodoRows } from './TodoRow';
import { OWNER_ALL, countToday, resolveOwner, useTodoOwner, visibleToday } from './todoFilters';
import { useTodoNavigate } from './useTodoNavigate';
import { useTodoSample } from './useTodoSample';

const PREVIEW_COUNT = 3;

const pad2 = (n: number) => String(n).padStart(2, '0');
/** 조회 시각 기준 표기 — 'M/D HH:MM 조회 기준'(고정 시각 문구 없음) */
function fetchedLabel(ms: number): string {
  if (!ms) return '조회 중';
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad2(d.getHours())}:${pad2(d.getMinutes())} 조회 기준`;
}

export function TodayFocusBanner() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { todos, isLoading, isError, error, fetchedAt, refetch } = useTodoSample({ scope: 'today' });
  const [storedOwner] = useTodoOwner();
  const { data: members } = useCompanyMembers();
  const goto = useTodoNavigate();

  const owner = resolveOwner(storedOwner, todos);
  const ownerName = useMemo(
    () => (owner ? (members ?? []).find((m) => String(m.memberId) === owner)?.memberName ?? `담당자 ${owner}` : '전체'),
    [owner, members],
  );
  const filter = { owner };
  const { red: RED, yellow: YELLOW } = visibleToday(todos, filter);
  const total = countToday(todos, filter);

  const top = RED.slice(0, PREVIEW_COUNT);
  const restRed = RED.slice(PREVIEW_COUNT);
  const hiddenCount = restRed.length + YELLOW.length;
  const showOwner = owner === OWNER_ALL;


  return (
    <Card className="[&>.body]:p-0" aria-label="오늘 챙길 일">
      {/* 머리줄 */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-5 pb-3.5 pt-4">
        <h3 className="text-heading-sm font-bold text-text">오늘 챙길 일</h3>
        {!isError && total > 0 && (
          <Badge status="accent">{total}</Badge>
        )}
        <span className="text-caption text-text-muted">{fetchedLabel(fetchedAt)} · 담당자 {ownerName}</span>
        <span className="flex-1" />
        <Button onClick={() => navigate('/todo')}>나의 할일 보기<ChevronRightIcon /></Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3 px-5 py-4" aria-busy="true">
          {Array.from({ length: PREVIEW_COUNT }, (_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}
        </div>
      ) : isError ? (
        // 오류·신호 누락은 장애 — 샘플·로컬 값으로 대체하지 않는다.
        // 홈 안의 한 영역이라 큰 상태 블록 대신 인라인 알림 + [다시 시도](D-07 §17.2 부분·인라인).
        <div className="flex flex-wrap items-center justify-center gap-3 px-5 py-6">
          <StatusHint tone="error">
            {error instanceof SignalMissingError ? error.message : '할일을 불러오지 못했어요'}
          </StatusHint>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>다시 시도</Button>
        </div>
      ) : total === 0 ? (
        <div className="flex items-center justify-center gap-2 px-5 py-7 text-body-md text-text-muted">
          오늘 챙길 일이 없어요 ✓
          <TextLink onClick={() => navigate('/todo/week')}>이번 주 할 일 ›</TextLink>
        </div>
      ) : (
        <>
          <TodoRows items={top} showOwner={showOwner} onNavigate={goto} />
          {open && (
            <>
              <TodoRows items={restRed} prevSite={top[top.length - 1]?.site} showOwner={showOwner} onNavigate={goto} />
              {YELLOW.length > 0 && (
                <>
                  <div className="flex items-center gap-1.5 border-t border-border bg-group-subtle px-5 py-2 text-caption font-bold text-text-secondary">
                    <SignalDot color="yellow" size={8} />오늘 일정
                  </div>
                  <TodoRows items={YELLOW} showOwner={showOwner} onNavigate={goto} />
                </>
              )}
            </>
          )}
          {hiddenCount > 0 && (
            // 더 보기 줄 — 기존 알약 디자인 유지(색·크기·모서리는 토큰). 줄 전체가 누르는 영역이라 native button.
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center border-t border-border px-5 py-3.5 transition-colors hover:bg-group-subtle"
            >
              <span className="ml-auto inline-flex h-8 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-body-sm font-bold text-info-600">
                {open ? '접기' : `${hiddenCount}건 더 보기`}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={open ? 'rotate-180' : ''} aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
          )}
        </>
      )}
    </Card>
  );
}
